import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useStreamCategories } from "./useStreamCategories";
import { useStreamBracket } from "./useStreamBracket";
import { useTransparentBody } from "./useTransparentBody";
import StreamControls from "./StreamControls";

// Page overlay stream — duel en cours (fond transparent, source
// navigateur OBS). URL : /stream/duel?category=women1 (&hide=1 une fois
// configurée). Reste affichée sur le résultat du dernier duel tranché
// jusqu'à ce qu'un NOUVEAU duel soit marqué actif (évite un écran vide
// entre deux duels).

const COLOR_HEX = { blue: "#1976d2", red: "#d32f2f" };

function findMatch(bracket, matchId) {
    if (!bracket || !matchId) return null;
    if (bracket.smallFinal && bracket.smallFinal.id === matchId) return bracket.smallFinal;
    for (const round of bracket.rounds || []) {
        const m = round.find((x) => x.id === matchId);
        if (m) return m;
    }
    return null;
}

function formatGap(ms) {
    if (ms == null) return null;
    return `${(ms / 1000).toFixed(2)} s`;
}

function Side({ athlete, color, isWinner, gapLabel, decided }) {
    if (!athlete) return <div style={sideStyle}>—</div>;
    return (
        <div style={{ ...sideStyle, opacity: decided && !isWinner ? 0.55 : 1 }}>
            <div style={{ ...swatchStyle, background: COLOR_HEX[color] || "#555" }} />
            <div style={bibStyle}>{athlete.bib}</div>
            <div style={nameStyle}>
                {isWinner ? "🏆 " : ""}
                {athlete.name}
            </div>
            {decided && !isWinner && gapLabel ? <div style={gapStyle}>+{gapLabel}</div> : null}
        </div>
    );
}

const sideStyle = { display: "flex", alignItems: "center", gap: 14, padding: "10px 0" };
const swatchStyle = { width: 18, height: 18, borderRadius: 4, flexShrink: 0 };
const bibStyle = { fontSize: 22, fontWeight: 800, color: "#fff", minWidth: 40 };
const nameStyle = { fontSize: 34, fontWeight: 800, color: "#fff", textShadow: "0 2px 6px rgba(0,0,0,0.85)" };
const gapStyle = { fontSize: 20, color: "#ffca28", fontWeight: 700, marginLeft: 8 };

export default function StreamDuel() {
    useTransparentBody();
    const [params, setParams] = useSearchParams();
    const category = params.get("category") || "";
    const hidden = params.get("hide") === "1";

    const categories = useStreamCategories();
    // Poll un peu plus rapide que les autres pages : c'est l'écran
    // "action" pendant le live.
    const bracket = useStreamBracket(category, 1200);

    // Le duel actif disparaît de bracket.activeMatchId dès qu'il est
    // tranché (voir finals.js/set-winner) — on "épingle" le dernier
    // matchId actif connu pour continuer à afficher son résultat jusqu'à
    // ce qu'un nouveau duel démarre, plutôt que de retomber sur un écran
    // vide entre deux duels.
    const [pinnedMatchId, setPinnedMatchId] = useState(null);
    useEffect(() => {
        if (bracket?.activeMatchId) setPinnedMatchId(bracket.activeMatchId);
    }, [bracket?.activeMatchId]);

    const setCategory = (value) => {
        const next = new URLSearchParams(params);
        if (value) next.set("category", value);
        else next.delete("category");
        setParams(next, { replace: true });
    };

    const displayedMatchId = bracket?.activeMatchId || pinnedMatchId;
    const match = findMatch(bracket, displayedMatchId);
    const isLive = !!bracket?.activeMatchId && !match?.winner;
    const decided = !!match?.winner;
    const gapLabel = formatGap(match?.arrivalGapMs);
    const isWinnerA = decided && match.athleteA && match.winner.fiscode === match.athleteA.fiscode;
    const isWinnerB = decided && match.athleteB && match.winner.fiscode === match.athleteB.fiscode;

    return (
        <div style={{ minHeight: "100vh", background: "transparent", padding: 24, fontFamily: "sans-serif" }}>
            <StreamControls categories={categories} category={category} onCategoryChange={setCategory} hidden={hidden} />

            {match && (
                <div style={{ maxWidth: 560, background: "rgba(0,0,0,0.55)", borderRadius: 14, padding: "18px 24px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff", opacity: 0.85 }}>{match.label}</div>
                        {isLive && (
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 800,
                                    color: "#fff",
                                    background: "#c62828",
                                    padding: "3px 10px",
                                    borderRadius: 999,
                                }}
                            >
                                ● EN DIRECT
                            </div>
                        )}
                    </div>

                    <Side athlete={match.athleteA} color={match.colorA} isWinner={isWinnerA} gapLabel={gapLabel} decided={decided} />
                    <div style={{ height: 1, background: "rgba(255,255,255,0.25)", margin: "4px 0" }} />
                    <Side athlete={match.athleteB} color={match.colorB} isWinner={isWinnerB} gapLabel={gapLabel} decided={decided} />

                    {decided && (match.photoFinish || match.juryDecision) && (
                        <div style={{ marginTop: 10, fontSize: 13, color: "#ffca28", fontWeight: 700 }}>
                            {match.photoFinish ? "🏁 Photo finish" : "⚖️ Décision du jury"}
                        </div>
                    )}
                </div>
            )}

            {!match && category && <div style={{ color: "#fff" }}>Aucun duel actif pour l'instant.</div>}
        </div>
    );
}
